/**
 * useChatActions - 聊天操作 Hook
 * 提取自 ChatPage.tsx，包含发送、重试、删除、编辑等用户操作
 */

import { useCallback, useRef } from 'react';
import { Message, Conversation } from '@/types';
import { apiPost } from '@/lib/api';
import { rebuildMessagesWithVersionPath } from '@/services/conversationService';
import { useUIStore } from '@/stores';
// import { isPendingId } from '@/utils/messageUtils';

interface UseChatActionsOptions {
  currentConversationId: string | undefined;
  currentConversation: Conversation | undefined;
  conversationsRef: React.MutableRefObject<Conversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setSidebarProjectConversationsCache: (
    updater: (
      prev: Record<string, Conversation[]>
    ) => Record<string, Conversation[]>
  ) => void;
  updateConversationLoadingState: (
    conversationId: string,
    isLoading: boolean
  ) => void;
  stopRequest: (pendingId?: string) => string[];
  sendMessage: (
    content: string,
    conversationId: string,
    pendingId: string,
    projectId: string,
    agentId?: string | null,
    options?: any,
    lastMessageId?: number
  ) => Promise<void>;
  deleteConversationSession: (
    conversationId: string,
    messageId: string
  ) => Promise<void>;
  loadConversations: () => Promise<void>;
  loadConversationDetails: (conversationId: string, force?: boolean) => void;
  selectedAgent?: { id: string; name: string; logo?: string } | null;
  currentConversationAgent?: { id: string; name: string; logo?: string } | null;
}

/**
 * 根据 versionPath 遍历 treeData，找到当前路径上最深的 assistant 节点的 backendId
 */
function getDeepestAssistantIdByVersionPath(
  nodes: any[],
  versionPath: Record<number, number>,
  depth: number = 0
): number | null {
  if (!nodes || nodes.length === 0) return null;

  const selectedIndex = versionPath[depth] ?? 0;
  const selectedNode = nodes[selectedIndex] || nodes[0];

  // 递归查找最深的节点
  if (selectedNode.children && selectedNode.children.length > 0) {
    const deeperId = getDeepestAssistantIdByVersionPath(
      selectedNode.children,
      versionPath,
      depth + 1
    );
    if (deeperId) return deeperId;
  }

  // 如果是 assistant 节点，返回其 backendId 或 id
  if (selectedNode.role === 'assistant') {
    return typeof selectedNode.backendId === 'number'
      ? selectedNode.backendId
      : typeof selectedNode.id === 'number'
        ? selectedNode.id
        : null;
  }

  return null;
}

export function useChatActions({
  currentConversationId,
  currentConversation,
  conversationsRef,
  setConversations,
  setSidebarProjectConversationsCache,
  updateConversationLoadingState,
  stopRequest,
  deleteConversationSession,
  loadConversations,
  loadConversationDetails,
  selectedAgent,
  currentConversationAgent,
}: UseChatActionsOptions) {
  const setConversationsRef = useRef(setConversations);
  const setSidebarProjectConversationsCacheRef = useRef(
    setSidebarProjectConversationsCache
  );

  // 更新 refs
  setConversationsRef.current = setConversations;
  setSidebarProjectConversationsCacheRef.current =
    setSidebarProjectConversationsCache;

  /**
   * 停止请求
   */
  const handleStopRequest = useCallback(async () => {
    if (!currentConversationId) return;

    console.log(
      '🛑 handleStopRequest: 停止当前会话的流式输出:',
      currentConversationId
    );

    // 🔥 立即清除该会话的所有活跃流式消息（Stop 按钮立即消失）
    useUIStore.getState().clearActiveStreamingMessages(currentConversationId);

    // 🔥 只停止当前会话的请求，不影响其他 conversation 的任务
    const completionIds = stopRequest(currentConversationId);

    console.log(
      '🛑 从 EventSourceClient 获取到的 completionIds:',
      completionIds
    );

    // 🔥 直接使用 EventSourceClient 返回的 completionIds 调用后端 stop 接口
    if (completionIds.length > 0) {
      console.log('🛑 调用后端 stop 接口:', completionIds.length, '个');

      const stopPromises = completionIds.map(completionId => {
        console.log('🛑 停止 completion:', completionId);
        return apiPost(
          `/api/clients/chat/completions/${completionId}/stop`,
          null,
          { skipErrorToast: true }
        ).catch(err => {
          console.error('Failed to stop completion:', completionId, err);
        });
      });

      // 🔥 等待所有 stop 接口调用完成
      await Promise.all(stopPromises);

      // 🔥 延迟 400ms，等待后端处理完成
      await new Promise(resolve => setTimeout(resolve, 400));

      // 🔥 刷新当前 conversation 的后端数据
      console.log('🛑 刷新 conversation 数据:', currentConversationId);
      loadConversationDetails(currentConversationId, true);
    }

    // 更新 loading 状态
    updateConversationLoadingState(currentConversationId, false);

    // 更新消息的 isStreaming 状态
    setConversationsRef.current(prev =>
      prev.map(conversation =>
        conversation.id === currentConversationId
          ? {
              ...conversation,
              messages: (conversation.messages || []).map((message: any) => ({
                ...message,
                isStreaming: false,
              })),
            }
          : conversation
      )
    );

    // 更新 sidebar cache
    setSidebarProjectConversationsCacheRef.current(prev => {
      const newCache = { ...prev };
      let updated = false;
      Object.keys(newCache).forEach(pid => {
        const projectConvs = newCache[pid] || [];
        const convIndex = projectConvs.findIndex(
          conv => conv.id === currentConversationId
        );
        if (convIndex !== -1) {
          newCache[pid] = [...projectConvs];
          newCache[pid][convIndex] = {
            ...projectConvs[convIndex],
            isLoading: false,
            messages: (projectConvs[convIndex].messages || []).map(message => ({
              ...message,
              isStreaming: false,
            })),
          };
          updated = true;
        }
      });
      return updated ? newCache : prev;
    });
  }, [
    currentConversationId,
    stopRequest,
    updateConversationLoadingState,
    loadConversationDetails,
  ]);

  /**
   * 停止群聊中的 AI 流式输出
   */
  const handleStopGroupChat = useCallback(async () => {
    if (!currentConversationId) return;

    console.log('🛑 handleStopGroupChat: 停止群聊中所有 AI 流式输出');

    // 🔥 立即清除该会话的所有活跃流式消息（Stop 按钮立即消失）
    useUIStore.getState().clearActiveStreamingMessages(currentConversationId);

    // 🔥 直接停止所有活跃请求，并获取所有 completionId（从 EventSourceClient 直接获取）
    const completionIds = stopRequest();

    console.log(
      '🛑 从 EventSourceClient 获取到的 completionIds:',
      completionIds
    );

    // 🔥 直接使用 EventSourceClient 返回的 completionIds 调用后端 stop 接口
    if (completionIds.length > 0) {
      console.log('🛑 调用后端 stop 接口:', completionIds.length, '个');

      const stopPromises = completionIds.map(completionId => {
        console.log('🛑 停止 completion:', completionId);
        return apiPost(
          `/api/clients/chat/completions/${completionId}/stop`,
          null,
          { skipErrorToast: true }
        ).catch(err => {
          console.error('Failed to stop completion:', completionId, err);
        });
      });

      // 🔥 等待所有 stop 接口调用完成
      await Promise.all(stopPromises);

      // 🔥 延迟 400ms，等待后端处理完成
      await new Promise(resolve => setTimeout(resolve, 400));

      // 🔥 刷新当前 conversation 的后端数据
      console.log('🛑 刷新 conversation 数据:', currentConversationId);
      loadConversationDetails(currentConversationId, true);
    }

    // 更新消息状态
    setConversationsRef.current(prev =>
      prev.map(conversation =>
        conversation.id === currentConversationId
          ? {
              ...conversation,
              isLoading: false,
              messages: (conversation.messages || []).map((message: any) => ({
                ...message,
                isStreaming: false,
              })),
            }
          : conversation
      )
    );

    console.log('🛑 handleStopGroupChat: 完成');
  }, [currentConversationId, stopRequest, loadConversationDetails]);

  /**
   * 删除消息 - 打开确认对话框的处理函数
   * 注意：实际的对话框状态需要在组件中管理
   */
  const canDeleteMessage = useCallback(
    (message: Message): boolean => {
      return !!(currentConversationId && message.backendId);
    },
    [currentConversationId]
  );

  /**
   * 确认删除消息
   */
  const confirmDeleteMessage = useCallback(
    async (message: Message): Promise<boolean> => {
      // 🔥 调试日志：追踪删除问题
      console.log('🗑️ confirmDeleteMessage called', {
        currentConversationId,
        messageId: message.id,
        messageBackendId: message.backendId,
        backendIdType: typeof message.backendId,
        message,
      });

      if (!currentConversationId || !message.backendId) {
        // 🔥 前端直接删除虚拟节点（无需后端请求）
        // 这种节点是由于权限等问题而构建出来的虚拟提示性节点
        // 最简单的方式：强制从后端重新加载对话，虚拟节点不在后端，所以会消失
        if (currentConversationId) {
          console.log('🗑️ Deleting virtual node by reloading conversation:', {
            conversationId: currentConversationId,
            messageId: message.id,
          });
          // 强制重新加载对话，虚拟节点会被后端真实数据覆盖而消失
          loadConversationDetails(currentConversationId, true);
          return true;
        }
        return false;
      }

      try {
        // 检查是否只有一个版本
        const isOnlyVersion = message.multiVersion === 1;

        if (isOnlyVersion && message.previousMessageId) {
          // 如果只有一个版本，也删除父消息
          const parentMessage = (currentConversation?.messages || []).find(
            m => m.backendId === message.previousMessageId
          );
          if (parentMessage) {
            await deleteConversationSession(
              currentConversationId,
              parentMessage.backendId + ''
            );
          }
        } else {
          // 删除当前消息
          await deleteConversationSession(
            currentConversationId,
            message.backendId + ''
          );
        }

        // 🔥 修复：删除消息后清理 latestMessageInitMap 中缓存的 assistantMessageId
        // 否则 getLastAssistantMessageId 会返回已删除消息的 ID，导致新消息的 previousMessageId 错误
        // 这是导致 "Message xxx not found" 错误的根本原因
        useUIStore.getState().clearLatestMessageInit(currentConversationId);

        // 重新加载对话列表
        await loadConversations();
        return true;
      } catch (error) {
        console.error('❌ Failed to delete message:', error);
        return false;
      }
    },
    [
      currentConversationId,
      currentConversation,
      deleteConversationSession,
      loadConversations,
      loadConversationDetails,
    ]
  );

  /**
   * 切换消息版本（用于树形渲染）
   */
  const handleChangeChoiceForTree = useCallback(
    (depth: number, newVersionIndex: number) => {
      if (!currentConversationId) return;

      // 🔥 获取当前对话，用于计算新的 lastAssistantId
      const conversation = conversationsRef.current.find(
        c => c.id === currentConversationId
      );

      // 计算新的 versionPath
      const newVersionPath: Record<number, number> = {};
      if (conversation) {
        Object.keys(conversation.versionPath || {}).forEach(key => {
          const keyDepth = parseInt(key, 10);
          if (keyDepth < depth) {
            newVersionPath[keyDepth] = conversation.versionPath![keyDepth];
          }
        });
      }
      newVersionPath[depth] = newVersionIndex;

      // 🔥 关键修复：版本切换后更新 latestMessageInitMap
      // 根据新的 versionPath 找到当前路径上最后一个 assistant 消息的 backendId
      if (conversation?.treeData) {
        const lastAssistantId = getDeepestAssistantIdByVersionPath(
          conversation.treeData,
          newVersionPath
        );

        if (lastAssistantId) {
          const { setLatestMessageInit, getLatestMessageInit } =
            useUIStore.getState();
          const existing = getLatestMessageInit(currentConversationId);

          console.log('🔄 版本切换：更新 latestMessageInitMap', {
            conversationId: currentConversationId,
            oldAssistantId: existing?.assistantMessageId,
            newAssistantId: lastAssistantId,
            depth,
            newVersionIndex,
          });

          setLatestMessageInit({
            conversationId: currentConversationId,
            userMessageId: existing?.userMessageId || 0,
            assistantMessageId: lastAssistantId,
            pendingId: existing?.pendingId || '',
          });
        }
      }

      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== currentConversationId) {
            return conv;
          }

          // 清理当前深度之后的所有路径选择
          const updatedVersionPath: Record<number, number> = {};
          Object.keys(conv.versionPath || {}).forEach(key => {
            const keyDepth = parseInt(key, 10);
            if (keyDepth < depth) {
              updatedVersionPath[keyDepth] = conv.versionPath![keyDepth];
            }
          });
          updatedVersionPath[depth] = newVersionIndex;

          // 重新构建 messages
          let newMessages = conv.messages;
          if (conv.treeData) {
            newMessages = rebuildMessagesWithVersionPath(
              conv.treeData,
              conv.conversationId,
              updatedVersionPath
            );
          }

          return {
            ...conv,
            versionPath: updatedVersionPath,
            messages: newMessages,
          };
        })
      );
    },
    [currentConversationId, conversationsRef]
  );

  /**
   * 切换消息版本（用于旧的 messages 数组渲染）
   */
  const handleChangeChoice = useCallback(
    (messageId: string, newActiveChoice: number) => {
      if (!currentConversationId) return;

      setConversationsRef.current(prev =>
        prev.map(conversation => {
          if (conversation.id !== currentConversationId) {
            return conversation;
          }

          const messages = conversation.messages || [];

          // 如果没有 treeData，使用旧逻辑
          if (!conversation.treeData) {
            return {
              ...conversation,
              messages: messages.map(msg =>
                msg.id === messageId
                  ? { ...msg, activeChoice: newActiveChoice }
                  : msg
              ),
            };
          }

          // 新逻辑：重新构建消息数组
          const clickedMessageIndex = messages.findIndex(
            msg => msg.id === messageId
          );
          if (clickedMessageIndex === -1) return conversation;

          const versionPath: Record<number, number> = {};
          for (let i = 0; i <= clickedMessageIndex; i++) {
            const msg = messages[i];
            versionPath[i] =
              i === clickedMessageIndex
                ? newActiveChoice
                : (msg.activeChoice ?? 0);
          }

          const newMessages = rebuildMessagesWithVersionPath(
            conversation.treeData,
            conversation.conversationId,
            versionPath
          );

          return {
            ...conversation,
            messages: newMessages,
          };
        })
      );
    },
    [currentConversationId]
  );

  /**
   * 清理无效的 treeData 节点（没有 backendId 的临时节点）
   */
  const pruneInvalidTreeNodes = useCallback((nodes: any[]): any[] => {
    return nodes
      .filter((node: any) => {
        const hasRealId = typeof node.id === 'number';
        const hasBackendIdProp = typeof node.backendId === 'number';
        return hasRealId || hasBackendIdProp;
      })
      .map((node: any) => {
        const newNode = { ...node };
        if (Array.isArray(node.children) && node.children.length > 0) {
          newNode.children = pruneInvalidTreeNodes(node.children);
        }
        return newNode;
      });
  }, []);

  /**
   * 在 treeData 中查找节点的后端 ID
   */
  const findBackendIdInTree = useCallback(
    (nodes: any[], tempId: any, role: 'user' | 'assistant'): number | null => {
      for (const node of nodes) {
        if (String(node.id) === String(tempId) && node.role === role) {
          return typeof node.id === 'number' ? node.id : null;
        }
        if (node.children && node.children.length > 0) {
          const found = findBackendIdInTree(node.children, tempId, role);
          if (found !== null) return found;
        }
      }
      return null;
    },
    []
  );

  /**
   * 获取当前 agent 信息
   */
  const getAgentInfo = useCallback(() => {
    const agent = currentConversationAgent || selectedAgent;
    return agent
      ? {
          id: agent.id,
          name: agent.name,
          logo: agent.logo,
        }
      : undefined;
  }, [currentConversationAgent, selectedAgent]);

  return {
    handleStopRequest,
    handleStopGroupChat,
    canDeleteMessage,
    confirmDeleteMessage,
    handleChangeChoiceForTree,
    handleChangeChoice,
    pruneInvalidTreeNodes,
    findBackendIdInTree,
    getAgentInfo,
  };
}

export default useChatActions;
